import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { 
  AppBar, 
  Box, 
  Toolbar, 
  Button,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  CircularProgress,
  Divider,
  Drawer,
  useTheme,
  useMediaQuery,
  List,
  ListItem
} from '@mui/material';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import SettingsIcon from '@mui/icons-material/Settings';
import LogoutIcon from '@mui/icons-material/Logout';
import DashboardIcon from '@mui/icons-material/Dashboard';
import GroupIcon from '@mui/icons-material/Group';
import MenuIcon from '@mui/icons-material/Menu';

const Navbar = () => {
  const navigate = useNavigate();
  const { user, logout, loading } = useAuth();
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  React.useEffect(() => {
    console.log('Navbar user data:', {
      fullUser: user,
      email: user?.email,
      role: user?.role,
      accountType: user?.accountType
    });
  }, [user]);

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleAccountClick = () => {
    navigate('/account');
    handleMenuClose();
  };

  const handleLogout = async () => {
    try {
      await logout();
      handleMenuClose();
      navigate('/login');
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  const isCorporateUser = (user) => {
    if (!user) return false;
    
    const isAdmin = user.role === 'admin';
    const isCorporate = user.role === 'corporate';
    const isSchoolAdmin = user.role === 'school_admin';
    
    console.log('isCorporateUser check:', {
      user: user,
      isAdmin,
      isCorporate,
      isSchoolAdmin,
      result: isAdmin || isCorporate || isSchoolAdmin
    });
    
    return isAdmin || isCorporate || isSchoolAdmin;
  };

  const canAccessCredits = (user) => {
    if (!user) return false;
    
    // Allow access for individual users who signed up directly
    if (user.role === 'individual' && !user.invitedBy) {
      return true;
    }
    
    // Allow access for admin/corporate/school_admin users
    return ['admin', 'corporate', 'school_admin'].includes(user.role);
  };

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  // Mobile drawer content
  const mobileDrawerContent = (
    <Box sx={{ width: 250, pt: 2 }}>
      <List>
        {user && (
          <>
            <ListItem button onClick={() => { navigate('/dashboard'); setMobileOpen(false); }}>
              <ListItemIcon><DashboardIcon /></ListItemIcon>
              <ListItemText primary="Dashboard" />
            </ListItem>
            <ListItem 
              button 
              onClick={() => { navigate('/quiz'); setMobileOpen(false); }}
              sx={{ 
                color: '#ff6b6b',
                '& .MuiListItemIcon-root': { color: '#ff6b6b' }
              }}
            >
              <ListItemIcon><DashboardIcon /></ListItemIcon>
              <ListItemText primary="Take Quiz" />
            </ListItem>
            {isCorporateUser(user) && (
              <ListItem button onClick={() => { navigate('/team'); setMobileOpen(false); }}>
                <ListItemIcon><GroupIcon /></ListItemIcon>
                <ListItemText primary={user.role === 'school_admin' ? 'Students' : 'Team'} />
              </ListItem>
            )}
            {(user?.role === 'corporate' || user?.role === 'admin' || user?.role === 'school_admin') && (
              <ListItem button onClick={() => { navigate('/groups'); setMobileOpen(false); }}>
                <ListItemIcon><GroupIcon /></ListItemIcon>
                <ListItemText primary="Groups" />
              </ListItem>
            )}
            {canAccessCredits(user) && (
              <ListItem button onClick={() => { navigate('/credits'); setMobileOpen(false); }}>
                <ListItemIcon><DashboardIcon /></ListItemIcon>
                <ListItemText primary="Credits" />
              </ListItem>
            )}
            <Divider sx={{ my: 1 }} />
            <ListItem button onClick={() => { handleAccountClick(); setMobileOpen(false); }}>
              <ListItemIcon><SettingsIcon /></ListItemIcon>
              <ListItemText primary="Account Settings" />
            </ListItem>
            <ListItem button onClick={() => { handleLogout(); setMobileOpen(false); }}>
              <ListItemIcon><LogoutIcon /></ListItemIcon>
              <ListItemText primary="Sign Out" />
            </ListItem>
          </>
        )}
      </List>
    </Box>
  );

  // Show loading state
  if (loading) {
    return (
      <AppBar position="static" sx={{ backgroundColor: 'white', boxShadow: 1 }}>
        <Toolbar sx={{ justifyContent: 'center' }}>
          <CircularProgress size={24} />
        </Toolbar>
      </AppBar>
    );
  }

  return (
    <AppBar position="static" sx={{ backgroundColor: 'white', boxShadow: 1 }}>
      <Toolbar sx={{ justifyContent: 'space-between' }}>
        {/* Logo */}
        <Link to="/">
          <img 
            src={require('../assets/images/logo.png')} 
            alt="Learning Style Quiz" 
            style={{ 
              height: '50px',
              marginTop: '10px',
              marginBottom: '10px'
            }} 
          />
        </Link>

        {/* Navigation Links */}
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          {user ? (
            <>
              {/* Show buttons only on desktop */}
              {!isMobile ? (
                <>
                  <Button 
                    component={Link} 
                    to="/dashboard"
                    sx={{ 
                      color: '#000',
                      '&:hover': { backgroundColor: 'transparent' }
                    }}
                  >
                    Dashboard
                  </Button>
                  <Button 
                    component={Link} 
                    to="/quiz"
                    sx={{ 
                      backgroundColor: '#ff6b6b',
                      color: '#fff',
                      '&:hover': { backgroundColor: '#ff5252' }
                    }}
                  >
                    Take Quiz
                  </Button>
                  {isCorporateUser(user) && (
                    <Button 
                      component={Link} 
                      to="/team"
                      sx={{ 
                        color: '#000',
                        '&:hover': { backgroundColor: 'transparent' }
                      }}
                    >
                      {user.role === 'school_admin' ? 'Students' : 'Team'}
                    </Button>
                  )}
                  {(user?.role === 'corporate' || user?.role === 'admin' || user?.role === 'school_admin') && (
                    <Button 
                      component={Link} 
                      to="/groups"
                      sx={{ 
                        color: '#000',
                        '&:hover': { backgroundColor: 'transparent' }
                      }}
                    >
                      Groups
                    </Button>
                  )}
                  {canAccessCredits(user) && (
                    <Button 
                      component={Link} 
                      to="/credits"
                      sx={{ 
                        color: '#000',
                        '&:hover': { backgroundColor: 'transparent' }
                      }}
                    >
                      Credits
                    </Button>
                  )}
                </>
              ) : null}
              
              {/* Show hamburger on mobile, account icon on desktop */}
              <IconButton
                onClick={isMobile ? handleDrawerToggle : handleMenuOpen}
                sx={{ 
                  color: '#000',
                  '&:hover': { backgroundColor: 'transparent' }
                }}
              >
                {isMobile ? <MenuIcon /> : <AccountCircleIcon />}
              </IconButton>
            </>
          ) : (
            <>
              <Button 
                component={Link} 
                to="/login"
                sx={{ 
                  color: '#000',
                  '&:hover': { backgroundColor: 'transparent' }
                }}
              >
                Login
              </Button>
              <Button 
                component={Link} 
                to="/register"
                sx={{ 
                  color: '#000',
                  '&:hover': { backgroundColor: 'transparent' }
                }}
              >
                Register
              </Button>
            </>
          )}
        </Box>

        {/* Keep existing menu for desktop */}
        {!isMobile && (
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
            onClick={handleMenuClose}
            PaperProps={{
              sx: { minWidth: '200px', mt: 1.5 }
            }}
          >
            <MenuItem disabled sx={{ opacity: 0.7 }}>
              <ListItemText 
                primary={user?.email}
                secondary={user?.role === 'school_admin' ? user?.schoolName : ''}
                primaryTypographyProps={{
                  variant: 'body2',
                  style: { fontStyle: 'italic' }
                }}
              />
            </MenuItem>
            <Divider />
            <MenuItem onClick={() => { navigate('/dashboard'); handleMenuClose(); }}>
              <ListItemIcon>
                <DashboardIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Dashboard</ListItemText>
            </MenuItem>
            <MenuItem onClick={handleAccountClick}>
              <ListItemIcon>
                <SettingsIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Account Settings</ListItemText>
            </MenuItem>
            <Divider />
            <MenuItem onClick={handleLogout}>
              <ListItemIcon>
                <LogoutIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Sign Out</ListItemText>
            </MenuItem>
          </Menu>
        )}

        {/* Mobile drawer */}
        <Drawer
          anchor="right"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: 250 }
          }}
        >
          {mobileDrawerContent}
        </Drawer>
      </Toolbar>
    </AppBar>
  );
};

export default Navbar;